import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Badge, Spinner } from 'react-bootstrap';
import { Landmark, Plus, Edit2, Trash2, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { useFeatures } from '../../../context/FeatureContext';
import { fetchConnections, patchFeedAccount } from '../../../services/BankConnectionService';
import axios from '../../../services/AxiosInstance';
import { getApiUrl } from '../../../utils/ApiUtils';

/**
 * UnifiedBankAccounts — ONE list where each row is a PHYSICAL bank account,
 * merging what used to be three overlapping surfaces (mappings grid,
 * "Omappade bankkonton" card, feed panel). The same account seen via CSV
 * import and via the PSD2 feed is one row, matched on normalized digits
 * (clearing+konto vs BBAN suffix).
 *
 * Each row answers the three questions a user actually has:
 *   1. Which account is this? (bank, clearing · konto, IBAN)
 *   2. How do transactions arrive? (live feed w/ health + sync, or CSV only)
 *   3. Where is it booked? (BAS account chip, or a fix-it CTA)
 */

const digits = (s) => String(s || '').replace(/\D/g, '');

/** Two identities are the same physical account when one's digit string ends with the other's. */
const sameAccount = (a, b) => {
    if (!a || !b) return false;
    return a === b || (a.length >= 7 && b.endsWith(a)) || (b.length >= 7 && a.endsWith(b));
};

const FEED_BADGES = {
    expiring: { variant: 'warning', label: 'Förnyas snart' },
    expired: { variant: 'danger', label: 'Utgången' },
    error: { variant: 'danger', label: 'Hämtningsfel' },
    pending_auth: { variant: 'secondary', label: 'Väntar på BankID' },
};

const UnifiedBankAccounts = ({ companyId, mappings, unmappedAccounts, onCreateMapping, onEditMapping, onDeleteMapping, onChanged }) => {
    const { features } = useFeatures();
    const hasBankFeed = Boolean(features?.bank_feed);

    const [connections, setConnections] = useState([]);
    const [attention, setAttention] = useState([]);
    const [busy, setBusy] = useState(null);

    const loadFeed = useCallback(async () => {
        if (!hasBankFeed) return;
        try {
            setConnections(await fetchConnections(companyId));
        } catch {
            // Feed info is progressive enhancement; the list still renders.
        }
    }, [companyId, hasBankFeed]);

    useEffect(() => {
        loadFeed();
    }, [loadFeed]);

    // Unreconciled workload per account — the "is work piling up here?" signal.
    useEffect(() => {
        axios
            .get(getApiUrl(`${companyId}/accounting/reconciliation/banking/statements/attention-count?by=account`), { withCredentials: true })
            .then(({ data }) => setAttention(data.accounts || []))
            .catch(() => {});
    }, [companyId]);

    const attentionFor = (row) => {
        const ident = (c, a) => digits(c) + digits(a);
        const rowIds = [
            row.mapping && ident(row.mapping.clearing_number, row.mapping.account_number),
            row.stats && ident(row.stats.clearing_number, row.stats.account_number),
            row.feed && (digits(row.feed.bban) || ident(row.feed.clearing_number, row.feed.account_number)),
        ].filter(Boolean);
        return attention
            .filter((a) => rowIds.some((id) => sameAccount(id, ident(a.clearing_number, a.account_number))))
            .reduce((n, a) => n + a.needs_attention, 0);
    };

    // ------------------------------------------------------------------
    // Merge the three sources into physical-account rows
    // ------------------------------------------------------------------
    const rows = useMemo(() => {
        const out = [];
        const feedAccounts = connections.flatMap((conn) =>
            (conn.accounts || []).map((a) => ({ ...a, _conn: conn }))
        );

        // Pre-merge unmapped identities that are the SAME physical account seen
        // through different rails (CSV: split clearing+konto; feed: full BBAN).
        // Keep the split identity for display, combine stats, prefer a real
        // bank name over placeholders.
        const mergedUnmapped = [];
        for (const u of unmappedAccounts || []) {
            const id = digits(u.clearing_number) + digits(u.account_number);
            const twin = mergedUnmapped.find((x) => sameAccount(digits(x.clearing_number) + digits(x.account_number), id));
            if (!twin) {
                mergedUnmapped.push({ ...u });
                continue;
            }
            twin.transaction_count = (Number(twin.transaction_count) || 0) + (Number(u.transaction_count) || 0);
            if (u.first_transaction && (!twin.first_transaction || u.first_transaction < twin.first_transaction)) twin.first_transaction = u.first_transaction;
            if (u.last_transaction && (!twin.last_transaction || u.last_transaction > twin.last_transaction)) twin.last_transaction = u.last_transaction;
            if (!twin.clearing_number && u.clearing_number) {
                twin.clearing_number = u.clearing_number;
                twin.account_number = u.account_number;
            }
            if ((!twin.bank_name || /okänd/i.test(twin.bank_name)) && u.bank_name) twin.bank_name = u.bank_name;
        }
        const usedFeed = new Set();
        const usedUnmapped = new Set();

        const idOfMapping = (m) => digits(m.clearing_number) + digits(m.account_number);
        const idOfFeed = (a) =>
            digits(a.bban) || digits(a.clearing_number) + digits(a.account_number) || digits(a.account_number);
        const idOfUnmapped = (u) => digits(u.clearing_number) + digits(u.account_number);

        for (const m of mappings || []) {
            const id = idOfMapping(m);
            const feed = feedAccounts.find((a) => !usedFeed.has(a.connection_account_id) && sameAccount(id, idOfFeed(a)));
            if (feed) usedFeed.add(feed.connection_account_id);
            const unmapped = mergedUnmapped.find((u, i) => !usedUnmapped.has(i) && sameAccount(id, idOfUnmapped(u)));
            if (unmapped) usedUnmapped.add(mergedUnmapped.indexOf(unmapped));
            out.push({ key: `m-${m.mapping_id}`, mapping: m, feed, stats: unmapped });
        }
        mergedUnmapped.forEach((u, i) => {
            if (usedUnmapped.has(i)) return;
            const id = idOfUnmapped(u);
            const feed = feedAccounts.find((a) => !usedFeed.has(a.connection_account_id) && sameAccount(id, idOfFeed(a)));
            if (feed) usedFeed.add(feed.connection_account_id);
            out.push({ key: `u-${i}`, mapping: null, feed, stats: u });
        });
        for (const a of feedAccounts) {
            if (usedFeed.has(a.connection_account_id)) continue;
            out.push({ key: `f-${a.connection_account_id}`, mapping: null, feed: a, stats: null });
        }
        return out;
    }, [mappings, unmappedAccounts, connections]);

    // Align a feed account's stored identity with the row's mapping so feed
    // rows land in the mapped history (only shown when they differ).
    const handleAlign = async (row) => {
        try {
            setBusy(row.feed.connection_account_id);
            await patchFeedAccount(companyId, row.feed.connection_account_id, {
                account_number: row.mapping.account_number,
                clearing_number: row.mapping.clearing_number,
            });
            toast.success('Bankflödet kopplat till kontot — transaktionerna hamnar nu i samma historik.');
            loadFeed();
            onChanged();
        } catch {
            toast.error('Kunde inte koppla bankflödet');
        } finally {
            setBusy(null);
        }
    };

    const identityOf = (row) => {
        if (row.mapping) return { clearing: row.mapping.clearing_number, account: row.mapping.account_number, bank: row.mapping.bank_name };
        if (row.stats) return { clearing: row.stats.clearing_number, account: row.stats.account_number, bank: row.stats.bank_name };
        return { clearing: row.feed.clearing_number, account: row.feed.account_number || row.feed.bban, bank: row.feed._conn.aspsp_name };
    };

    const needsAlignment = (row) =>
        row.mapping && row.feed && digits(row.feed.account_number) !== digits(row.mapping.account_number);

    return (
        <div className="modern-card">
            <div className="modern-card-header">
                <div>
                    <h2 className="section-title">
                        <Landmark size={24} /> Bankkonton
                    </h2>
                    <p className="section-subtitle">
                        Ett kort per bankkonto: hur transaktionerna kommer in och vilket konto i kontoplanen de bokförs mot.
                    </p>
                </div>
            </div>
            <div className="modern-card-body">
                {rows.length === 0 ? (
                    <div className="text-center py-5">
                        <Landmark size={40} style={{ color: '#9ca3af', marginBottom: 12 }} />
                        <h5 style={{ color: '#6b7280' }}>Inga bankkonton ännu</h5>
                        <p className="text-muted" style={{ fontSize: 13 }}>
                            Anslut din bank under <Link to={`/${companyId}/settings/bank-connections`}>Bankanslutningar</Link>{' '}
                            eller importera ett kontoutdrag under Bankhändelser.
                        </p>
                    </div>
                ) : (
                    <div style={{ display: 'grid', gap: 12 }}>
                        {rows.map((row) => {
                            const id = identityOf(row);
                            const conn = row.feed?._conn;
                            const feedBadge = conn ? FEED_BADGES[conn.status] : null;
                            const syncing = conn && busy === conn.connection_id;
                            return (
                                <div key={row.key} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 16px' }}>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                                        {/* 1. Identity */}
                                        <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                                            <div style={{ fontWeight: 600, color: '#0f172a' }}>
                                                {row.mapping?.account_nickname || id.bank || 'Bankkonto'}
                                            </div>
                                            <div
                                                style={{ fontSize: 13, color: '#6b7280', fontVariantNumeric: 'tabular-nums' }}
                                                title={id.clearing ? `Clearingnummer ${id.clearing} · Kontonummer ${id.account}` : undefined}
                                            >
                                                {id.clearing ? `${id.clearing}-` : ''}{id.account}
                                            </div>
                                            {row.feed?.iban && (
                                                <div style={{ fontSize: 11, color: '#9ca3af', fontVariantNumeric: 'tabular-nums' }}>
                                                    IBAN {row.feed.iban}
                                                </div>
                                            )}
                                            {row.stats && (
                                                <div style={{ fontSize: 11, color: '#9ca3af' }}>
                                                    {row.stats.transaction_count} transaktioner · {row.stats.first_transaction} – {row.stats.last_transaction}
                                                </div>
                                            )}
                                            {(() => {
                                                const n = attentionFor(row);
                                                return n > 0 ? (
                                                    <div>
                                                        <Link
                                                            to={`/${companyId}/bankstatements`}
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: 5,
                                                                fontSize: 12,
                                                                fontWeight: 600,
                                                                color: '#b45309',
                                                                background: '#fffbeb',
                                                                borderRadius: 999,
                                                                padding: '3px 10px',
                                                                textDecoration: 'none',
                                                            }}
                                                            title="Öppna Bankhändelser och hantera raderna"
                                                        >
                                                            {n} {n === 1 ? 'transaktion' : 'transaktioner'} att hantera
                                                        </Link>
                                                    </div>
                                                ) : null;
                                            })()}
                                        </div>

                                        {/* 2. Source — passive indicator only. Everything
                                            connection-related (sync, renewal, toggles) lives in
                                            Settings → Bankanslutningar; this chip just says how
                                            transactions arrive and links there. */}
                                        <div style={{ flex: '0 1 auto', fontSize: 13 }}>
                                            {conn ? (
                                                <Link
                                                    to={`/${companyId}/settings/bank-connections`}
                                                    title="Hantera bankkopplingen under Inställningar"
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: 6,
                                                        fontSize: 12,
                                                        fontWeight: 600,
                                                        padding: '4px 10px',
                                                        borderRadius: 999,
                                                        textDecoration: 'none',
                                                        color: feedBadge ? '#b45309' : '#15803d',
                                                        background: feedBadge ? '#fffbeb' : '#f0fdf4',
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            width: 7,
                                                            height: 7,
                                                            borderRadius: '50%',
                                                            background: feedBadge ? '#f59e0b' : '#16a34a',
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                    {feedBadge ? feedBadge.label : 'Automatisk hämtning'}
                                                </Link>
                                            ) : (
                                                <span style={{ color: '#9ca3af', display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12 }}>
                                                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#d1d5db', flexShrink: 0 }} />
                                                    CSV-import
                                                    {hasBankFeed && (
                                                        <Link to={`/${companyId}/settings/bank-connections`} style={{ marginLeft: 4 }}>
                                                            Anslut bank
                                                        </Link>
                                                    )}
                                                </span>
                                            )}
                                        </div>

                                        {/* 3. Where it is booked */}
                                        <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                                            {row.mapping ? (
                                                <>
                                                    <span
                                                        style={{
                                                            fontSize: 12,
                                                            fontWeight: 600,
                                                            color: '#15803d',
                                                            background: '#f0fdf4',
                                                            borderRadius: 6,
                                                            padding: '4px 8px',
                                                        }}
                                                    >
                                                        {row.mapping.accounting_number} {row.mapping.accounting_name}
                                                    </span>
                                                    <Button
                                                        variant="link"
                                                        size="sm"
                                                        onClick={() => onEditMapping(row.mapping)}
                                                        title="Ändra BAS-konto"
                                                        style={{ color: '#6b7280', padding: 6, lineHeight: 1, borderRadius: 8 }}
                                                        onMouseEnter={(e) => (e.currentTarget.style.background = '#f3f4f6')}
                                                        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                                                    >
                                                        <Edit2 size={15} />
                                                    </Button>
                                                    <Button
                                                        variant="link"
                                                        size="sm"
                                                        onClick={() => onDeleteMapping(row.mapping.mapping_id)}
                                                        title="Ta bort mappningen"
                                                        style={{ color: '#dc2626', padding: 6, lineHeight: 1, borderRadius: 8 }}
                                                        onMouseEnter={(e) => (e.currentTarget.style.background = '#fee2e2')}
                                                        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                                                    >
                                                        <Trash2 size={15} />
                                                    </Button>
                                                </>
                                            ) : (
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    onClick={() =>
                                                        onCreateMapping({
                                                            bank_name: id.bank,
                                                            clearing_number: id.clearing || '',
                                                            account_number: id.account || '',
                                                        })
                                                    }
                                                >
                                                    <Plus size={13} className="me-1" /> Välj BAS-konto
                                                </Button>
                                            )}
                                        </div>
                                    </div>

                                    {needsAlignment(row) && (
                                        <div
                                            style={{
                                                marginTop: 10,
                                                paddingTop: 10,
                                                borderTop: '1px dashed #fde68a',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 10,
                                                fontSize: 12,
                                                color: '#92400e',
                                                flexWrap: 'wrap',
                                            }}
                                        >
                                            <Link2 size={14} />
                                            Bankflödet och CSV-historiken använder olika kontonummerformat — koppla ihop dem så hamnar allt i samma historik.
                                            <Button variant="warning" size="sm" onClick={() => handleAlign(row)} disabled={busy === row.feed.connection_account_id}>
                                                {busy === row.feed.connection_account_id ? <Spinner animation="border" size="sm" /> : 'Koppla ihop'}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default UnifiedBankAccounts;
